/**
 * Textos de plugin no i18n do app, registrados em tempo de execucao.
 *
 * O core nao pode traduzir frase de plugin — nao conhece as frases, e elas
 * chegam depois, com o metadata. E o plugin nao pode se virar sozinho sem
 * repetir, em cada tela sua, a escolha do idioma e a cadeia de fallback.
 *
 * Aqui o plugin DECLARA (`provides.i18n`) e o app REGISTRA no carregamento.
 * A partir dai qualquer parte do plugin — JSX de campo, balao de resultado,
 * frontendScript — pede `t("chave")` e recebe o texto no idioma da interface.
 *
 * Antes disto, o jeito era serializar o dicionario inteiro dentro de cada
 * string de JSX: o mesmo texto viajava duplicado em cada componente, e nao
 * havia como um script do plugin reusar a frase que a tela ja tinha.
 *
 * As funcoes levam o prefixo `i18n` de proposito: sao sete nomes que so fazem
 * sentido juntos, e o prefixo os mantem lado a lado no autocomplete de quem
 * escreve plugin.
 */

/** Textos de um idioma: chave → frase. */
export type LanguageStrings = Record<string, string>;

/**
 * O dicionario do plugin: idioma → textos.
 *
 * A chave do idioma segue o app ("pt", "en", "es", "ar", "tr"). Aceita
 * variante ("pt-BR"), mas o registro guarda pela raiz — quem escolhe "pt-BR"
 * e quem escolhe "pt" veem a mesma coisa, e ninguem precisa declarar as duas.
 */
export type PluginTranslations = Record<string, LanguageStrings>;

/** Idioma usado quando o do usuario nao foi declarado pelo plugin. */
const IDIOMA_RESERVA = "en";

/** So a raiz do idioma: "pt-BR" → "pt". */
export function i18nLanguageRoot(idioma?: string | null): string {
  return String(idioma || "").slice(0, 2).toLowerCase() || IDIOMA_RESERVA;
}

/**
 * Registro dos textos, por plugin.
 *
 * Vive num modulo so (nao numa instancia por tela) porque o registro acontece
 * uma vez, no carregamento do `/plugins`, e o consumo acontece em toda parte.
 */
const registro = new Map<string, PluginTranslations>();

/**
 * O i18next do app, quando injetado.
 *
 * Opcional de proposito: a traducao funciona sem ele (a cadeia de fallback
 * esta aqui). Com ele, os textos tambem viram um namespace do i18n do app, e
 * quem ja usa `i18n.t` alcanca as frases do plugin por `pluginId:chave`.
 */
let i18nDoApp: any = null;

/** O app injeta sua instancia de i18n (opcional). */
export function i18nUseApp(instancia: any): void {
  i18nDoApp = instancia || null;
  // Reaplica o que ja foi registrado: a ordem entre "app injeta i18n" e
  // "plugins carregam" nao e garantida — depende de quem responde primeiro.
  registro.forEach((textos, pluginId) => publicarNoApp(pluginId, textos));
}

function publicarNoApp(pluginId: string, textos: PluginTranslations): void {
  if (!i18nDoApp?.addResourceBundle) return;
  Object.keys(textos).forEach(idioma => {
    try {
      // `deep`/`overwrite`: recarregar o plugin (apos editar settings, por
      // exemplo) tem que trocar o texto, nao manter o antigo em silencio.
      i18nDoApp.addResourceBundle(i18nLanguageRoot(idioma), pluginId, textos[idioma], true, true);
    } catch {
      /* i18n do app indisponivel nao pode derrubar o carregamento de plugins */
    }
  });
}

/**
 * Registra (ou substitui) os textos de um plugin.
 *
 * Idempotente: chamada de novo com o mesmo pluginId troca o dicionario. E o
 * que acontece quando a lista de plugins e recarregada.
 */
export function i18nRegister(pluginId: string, textos?: PluginTranslations | null): void {
  if (!pluginId || !textos || typeof textos !== "object") return;

  const normalizado: PluginTranslations = {};
  Object.keys(textos).forEach(idioma => {
    normalizado[i18nLanguageRoot(idioma)] = textos[idioma];
  });

  registro.set(pluginId, normalizado);
  publicarNoApp(pluginId, normalizado);
}

/** Esquece os textos de um plugin (desinstalado/removido). */
export function i18nForget(pluginId: string): void {
  registro.delete(pluginId);
}

/** O que esta registrado — usado em teste e diagnostico. */
export function i18nRegistered(pluginId: string): PluginTranslations | undefined {
  return registro.get(pluginId);
}

/**
 * Traduz uma chave do plugin.
 *
 * Cadeia: idioma pedido → ingles → a propria chave. Devolver a chave em vez de
 * vazio e proposital: tela com "toolsIntro" escrito denuncia a traducao que
 * falta; tela em branco parece bug do sistema.
 *
 * `params` faz interpolacao simples de `{{nome}}` — sem plural nem formatacao,
 * que exigiriam trazer o i18next para dentro do SDK.
 */
export function i18nTranslate(
  pluginId: string,
  chave: string,
  idioma?: string | null,
  params?: Record<string, string | number>
): string {
  const textos = registro.get(pluginId);
  const raiz = i18nLanguageRoot(idioma);

  const frase = textos?.[raiz]?.[chave] ?? textos?.[IDIOMA_RESERVA]?.[chave] ?? chave;

  if (!params) return frase;
  return String(frase).replace(/\{\{\s*([\w.]+)\s*\}\}/g, (todo, nome) =>
    params[nome] === undefined ? todo : String(params[nome])
  );
}

/**
 * Um tradutor amarrado a um plugin e a um idioma.
 *
 * E o que vai nas props do JSX: dentro do componente do plugin se escreve
 * `t("usarResposta")`, sem repetir pluginId e idioma em cada chamada.
 */
export function i18nTranslator(
  pluginId: string,
  idioma?: string | null
): (chave: string, params?: Record<string, string | number>) => string {
  return (chave, params) => i18nTranslate(pluginId, chave, idioma, params);
}
